import { useState } from 'react'
//import { Platform } from 'react-native'
import { ChevronUp, ChevronRight, Plus } from '@tamagui/lucide-icons-2'
import { 
  ActionButton, Image, SimpleText, Text,
  View, Switch, XStack, YStack, XPPlatform as Platform, TZACardStyle 
} from '@my/ui'
import { color, MiscUtils, TZABrandGuidelines } from '@my/util'

import { shadowGenerator } from '../../../../utils/commonStyles'
import { InsightCharts } from '../insightChart'
import images from '../../../../assets/images'
import { InsightCardHeaderProps, InsightsCardProps } from './types'

//  Toggle Component
const Toggle = ({ isOn, setIsOn }) => {
  return (
    <XStack alignItems="center" gap={10}>
      <Switch
        p={5}
        size="$3"
        width={40}
        checked={isOn}
        borderWidth={1}
        onCheckedChange={setIsOn}
        backgroundColor={isOn ? color.lightRed : color.toggleGreen}
      >
        <Switch.Thumb backgroundColor={color.white} height={15} width={15} />
      </Switch>
      <SimpleText fontSize={15} fontWeight="600">
        {isOn ? 'Ignore' : 'Addressed'}
      </SimpleText>
    </XStack>
  )
}
// ActionButtons Section Component
const ActionButtonsSection = ({ isOn, setIsOn, toggleExpanded, isExpanded }) => (
  <XStack justifyContent="space-between">
    {/* <Toggle isOn={isOn ?? false} setIsOn={setIsOn} /> */}
    <ActionButton height={38} width={38} borderRadius={100} bg={color.lightGreen} onPress={toggleExpanded}>
      <Image src={isExpanded ? images.ArrowBack : images.ArrowNext} height={13} width={15} resizeMode="contain" />
    </ActionButton>
  </XStack>
)

// InsightsCard Component
export const ExpandableInsightsCard = (props: InsightsCardProps) => {
  const { title, value, tipsData, isLiked, isBookMarked, onPressAddTip, item, index, children, headerImage, extraHeaderComponent, maxWidth } = props || {}
  const [isExpanded, setIsExpanded] = useState(props.startExpanded || false)
  const [isOn, setIsOn] = useState(!item?.addressed)

  const tips = tipsData && Array.isArray(tipsData) ? <TipsSection tipsData={tipsData} /> : null

  return (
    <XStack style={{minWidth: 250, width: '100%'}}>
      <YStack
        //backgroundColor="white"
        borderRadius={10}
        {...shadowGenerator(0.2)}
        padding="$3"
        gap={20}
        hoverStyle={{backgroundColor: '#e7e7e7', backgroundImage: 'none'}}
        onPress={() => setIsExpanded((prev) => !prev)}
        style={{
          borderStyle: 'solid', borderWidth: 1, ...TZACardStyle, backgroundImage: 'none',
          ...(maxWidth ? {maxWidth} : {}),
          width: '100%'
        }}
      >
        <CardHeaderSection
          title={title}
          isExpanded={isExpanded}
          tip={value}
          headerImage={headerImage}
          extraHeaderComponent={extraHeaderComponent}
        />
        {isExpanded ? <>{children}{tips}</> : null}
      </YStack>
    </XStack>
  )
}

// CardHeaderSection Component
const CardHeaderSection = ({ title, isExpanded, tip, onPress, headerImage, extraHeaderComponent }: InsightCardHeaderProps) => {
  const { grey, tzaGreen } = color
  return (
    <ActionButton
      unstyled
      borderRadius={10}
      borderColor={color.lightGreen}
      onPress={onPress}
      minWidth={250}
      gap={10}
    >
      <YStack gap={5}>
        <XStack justifyContent="space-between">
          <XStack style={{gap: 10, flexShrink: 1}}>
            <XStack 
              style={{
                justifyContent: 'center', alignItems: 'center', borderRadius: 100, height: 45, width: 45,
                borderWidth: 1,
                borderColor: 'rgba(220,220,220, .15)'
              }} 
              {...MiscUtils.ui.shadowGenerator(0.03,5)}
            >
              <Image src={headerImage || images.keyInsight} style={{height: 24, width: 24}} resizeMode="contain" />
            </XStack>
            <YStack style={{justifyContent:'center', flexShrink: 1}}>
              <SimpleText
                numberOfLines={isExpanded ? undefined : 2}
                color={'rgb(73, 80, 87)'}
                fontSize={17}
                fontWeight={'700'}
              >
                {title}
              </SimpleText>
            </YStack>
          </XStack>
          {extraHeaderComponent || null}
          <ActionButton
            unstyled
            disabled
            color={grey}
            borderRadius={3}
            mt={13}
            backgroundColor={tzaGreen}
            icon={isExpanded ? ChevronUp : ChevronRight}
            style={{ height: 18, width: 18, alignItems: 'center', justifyContent: 'center', marginLeft: 10 }}
          />
        </XStack>
        <SimpleText
          numberOfLines={isExpanded ? undefined : 2}
          marginTop="$2"
          fontSize={16}
          color={'rgb(57, 57, 57)'}
        >
          {tip}
        </SimpleText>
      </YStack>
    </ActionButton>
  )
}

//  ExpandedDetails Component
const ExpandedDetails = (props: {
  tipsData: any[]
  isLiked: boolean
  isBookMarked: boolean
  onPressAddTip: (data) => void
  chartData: any[]
  index: number
}) => {
  const { tipsData, isLiked, isBookMarked, onPressAddTip, chartData, index } = props || {}
  return (
    <YStack gap={20}>
      {/* <InsightCharts width={Platform.OS === 'web' ? 600 : 320} item={chartData} index={index} /> */}
      <TipsSection tipsData={tipsData} />
      {/* <ActionsSection isLiked={isLiked} isBookMarked={isBookMarked} onPressAddTip={onPressAddTip} /> */}
      {/* <HelpfulnessSection /> */}
      {/* <ActionButton unstyled>
        <SimpleText
          $platform-native={{ fontSize: 15 }}
          fontSize={17}
          fontWeight="700"
          color={color.blue}
        >
          Learn More About Automated Savings
        </SimpleText>
      </ActionButton> */}
    </YStack>
  )
}

// TipsSection Component
const TipsSection = ({ tipsData }: { tipsData: any[] }) => (
  <YStack style={{}} gap={5}>
    <XStack alignItems="center" gap={5}>
      {/* <Image src={images.tips} style={{ height: 30, width: 23 }} resizeMode="contain" /> */}
      <SimpleText fontSize={17} fontWeight="700" color={TZABrandGuidelines.colorsMap.primaryGreen}>
        Tips
      </SimpleText>
    </XStack>
    <YStack gap={5}>
      {tipsData?.map((item, index) => (
        <XStack key={index}>
          <SimpleText color={color.grey} fontSize={15}>
            {'• '}
          </SimpleText>
          <SimpleText flex={1} color={color.grey} fontSize={15}>
            {item.tip}
          </SimpleText>
        </XStack>
      ))}
    </YStack>
  </YStack>
)

// ActionsSection Component
const ActionsSection = ({
  isLiked,
  isBookMarked,
  onPressAddTip,
}: {
  isLiked: boolean
  isBookMarked: boolean
  onPressAddTip: (data) => void
}) => (
  <XStack alignItems="center" justifyContent="space-between">
    <ActionButton
      unstyled
      onPress={onPressAddTip}
      borderRadius={100}
      flexDirection="row"
      bg={color.toggleGreen}
      height={40}
      paddingHorizontal={20}
      alignItems="center"
      justifyContent="center"
    >
      <Plus height={13} width={13} color={color.grey} />
      <SimpleText fontSize={14} fontWeight="600" color={color.grey}>
        Add This Tip to My Goals
      </SimpleText>
    </ActionButton>
    <XStack gap={10}>
      <ActionButton
        unstyled
        icon={<Image src={images.thumbsUp2} tintColor={isLiked ? color.blue : color.grey} />}
      />
      <ActionButton
        unstyled
        icon={<Image src={images.bookMark} tintColor={isBookMarked ? color.blue : color.grey} />}
      />
    </XStack>
  </XStack>
)

//  HelpfulnessSection Component
const HelpfulnessSection = () => (
  <YStack alignItems="center">
    <XStack
      alignItems="center"
      justifyContent="space-evenly"
      {...shadowGenerator(0.1)}
      bg={color.white}
      gap={20}
      padding={10}
    >
      <YStack alignItems="center" justifyContent="center">
        <SimpleText fontSize={14} fontWeight="700" color={color.grey}>
          Was this helpful?
        </SimpleText>
      </YStack>
      <XStack gap={10}>
        <ActionButton
          borderRadius={4}
          bg="transparent"
          borderColor={color.tzaGreen}
          borderWidth={1}
          height={34}
        >
          <SimpleText fontSize={14} fontWeight="600" color={color.tzaGreen}>
            Yes
          </SimpleText>
        </ActionButton>
        <ActionButton
          height={34}
          borderRadius={4}
          bg="transparent"
          borderColor={color.lightRed}
          borderWidth={1}
        >
          <SimpleText fontSize={14} fontWeight="600" color={color.lightRed}>
            No
          </SimpleText>
        </ActionButton>
      </XStack>
    </XStack>
  </YStack>
)
